/**
 * adapters/interviewPrepAdapter.js — RC 1.53 Phase 4.3A
 *
 * Thin wrapper around
 * `lib/interviewPrepPolling.js::generateInterviewPrepWithPolling`.
 *
 * Mirrors `cvVariantAdapter.js` (Phase 4.2). The adapter exists so the
 * section dispatches via the controller (gaining the no-op-on-in-
 * flight guard, lifecycle events, `STABILIZING` status, workspace-
 * wide Generate-lock, and the activity indicator) without changing
 * what the polling helper does on the wire.
 *
 * Strict scope (Phase 4.3A):
 *   • Wraps `generateInterviewPrepWithPolling` verbatim — same
 *     positional / keyword args, same callbacks, same return shape.
 *   • Does NOT touch idempotency keys (the section mints them).
 *   • Does NOT call any backend endpoint other than the polling
 *     helper (interview prep has NO finalize-charge step — charging
 *     is atomic inside the standalone orchestrator).
 *   • Does NOT write URL params (the section owns URL state and
 *     writes ?interview_prep_id={runId} synchronously on async_202).
 *   • Does NOT refresh credits (the section AWAITS
 *     `credits.refresh()` inside the stabilization chain).
 *   • Forwards the section's `onRunIdReady` callback verbatim so
 *     the synchronous URL write on async_202 still fires.
 *   • Lazy-imports the polling helper.
 */

export const ACTION_CODE = 'interview_prep';

export async function run({
  requestArgs,
  callbacks,
}) {
  if (!requestArgs)
    throw new Error('interviewPrepAdapter.run: requestArgs is required');
  if (typeof requestArgs !== 'object')
    throw new Error('interviewPrepAdapter.run: requestArgs must be an object');

  const { generateInterviewPrepWithPolling } = await import(
    /* webpackMode: "eager" */ '@/lib/interviewPrepPolling.js'
  );

  // The section's caller may have passed its own onRunIdReady on
  // requestArgs to write the URL synchronously. We chain into it
  // (without replacing it) and ALSO surface the controller's
  // onProgress callback so the activity indicator sees a polling
  // transition for the async_202 path.
  const sectionOnRunIdReady = requestArgs.onRunIdReady;
  const wrappedOnRunIdReady = (runId) => {
    try {
      if (callbacks?.onProgress) callbacks.onProgress('running', 'running');
      if (callbacks?.onPolling)  callbacks.onPolling();
    } catch { /* noop */ }
    if (typeof sectionOnRunIdReady === 'function') {
      try { sectionOnRunIdReady(runId); } catch { /* noop */ }
    }
  };

  return generateInterviewPrepWithPolling({
    ...requestArgs,
    onRunIdReady: wrappedOnRunIdReady,
  });
}

export const PHASE_4_3A_CONTRACT = Object.freeze({
  wrap_polling_helper:        'lib/interviewPrepPolling.js::generateInterviewPrepWithPolling',
  poll_resume_helper:         'lib/interviewPrepPolling.js::pollInterviewPrepUntilTerminal',
  url_param_contract:         ['interview_prep_id={id} (written SYNC on async_202)'],
  inflight_marker_key:        'inflight-interview-prep:${userId}:${jobId}',
  side_effects_owned_by_adapter: [],
  side_effects_owned_by_section: [
    'idempotency_key derivation (getOrMintIdempotencyKey)',
    'inflight marker write/patch/clear',
    'URL param writes (interview_prep_id) — synchronous on async_202 + on success',
    'cache invalidation (jobs, dashboard:bundle)',
    'awaited credits.refresh() inside the stabilization chain',
    'lifecycle markComplete after all stabilization lands',
    'cooldown start (interviewPrepCooldownKey)',
  ],
});
